import PropTypes from 'prop-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import { css } from '@emotion/react';

import Text from '../Text/Text';
import ExternalLink from '../ExternalLink/ExternalLink';

const ulCss = css`
  list-style: initial;
  margin-block-start: 1em;
  margin-block-end: 1em;
  padding-inline-start: 40px;

  li > p {
    margin-top: initial;
    margin-bottom: initial;
  }
`;

const imgCss = css`
  max-width: 100%;
`;

/**
 * Render CMS's rich text to React JSX.
 *
 * @see
 * [rich-text-react-renderer](https://github.com/contentful/rich-text/tree/master/packages/rich-text-react-renderer)
 */
function RichText({ text, textOptions }) {
  const defaultTextOptions = {
    renderNode: {
      [BLOCKS.PARAGRAPH](node, children) {
        return (
          <Text isMui={false} paragraph>
            {children}
          </Text>
        );
      },
      [BLOCKS.UL_LIST](node, children) {
        return <ul css={ulCss}>{children}</ul>;
      },
      [BLOCKS.EMBEDDED_ASSET](node) {
        const {
          description,
          file: { url, contentType },
          title,
        } = node.data.target;

        const mimeType = contentType;
        const mimeGroup = mimeType.split('/')[0];

        switch (mimeGroup) {
          case 'image':
            return <img src={url} alt={description ?? ''} css={imgCss} />;

          case 'application':
            return (
              <ExternalLink href={url}>{description ?? title}</ExternalLink>
            );

          default:
            console.error(
              `Trying to render an embedded asset which isn't supported (${title})`
            );

            return null;
        }
      },
      ...textOptions?.renderNode,
    },
    renderText: text => {
      /**
       * Contentful doesn't render line-breaks (<br />).
       * Therefore, we replace all newlines with line-breaks.
       *
       * Please note, we used to use flatMap in order to achieve it,
       * but it wasn't well-supported across different browsers:
       * text.split('\n').flatMap((text, i) => [i > 0 && <br key={i} />, text])
       */
      const hasLineBreaks = /\n/.exec(text);

      if (!hasLineBreaks) {
        return text;
      }

      const textWithLineBreaks = [];

      text.split('\n').forEach((content, i) => {
        if (i > 0) {
          textWithLineBreaks.push(<br key={i} />);
        }

        textWithLineBreaks.push(content);
      });

      return textWithLineBreaks;
    },
  };

  return <div>{documentToReactComponents(text, defaultTextOptions)}</div>;
}

RichText.propTypes = {
  /** Rich text content to render */
  text: PropTypes.object.isRequired,
  /** Custom render options */
  textOptions: PropTypes.object,
};

export default RichText;
